import { fetchReportListDownload } from '@/pages/ScheduledDeliverables/store/thunks';
import apiInstance from '@api/index';
import toastMessage from '@components/Toast';
import { createAsyncThunk } from '@reduxjs/toolkit';
import downloadFile from '@utils/downloadFile';
import downloadS3File from '@utils/downloadS3File';

import { MemoSubToggleModes } from '../../components/DeliverableMemos';
import { DeliverableReportModes } from '../../components/DeliverableReports';

import {
  AsyncThunkConfig,
  GenericResponse,
  RunDeliverableMemosParams,
  RunDeliverableResponseData,
  ScheduleDeliverableParams,
  ThunkError,
} from './types';
import { EntityJECurrenciesSuccessPayload } from '@/store/companyEntity/types';
import { fetchBulkTransactionTitles } from '@pages/HedgeAccountingAdmin/store/hedgeAccountingSlice';
import { BulkTransactionTitlesRequest } from '@pages/HedgeAccountingAdmin/store/types';
import { AppDispatch } from '@store/store';

export const scheduleDeliverable = createAsyncThunk<
  GenericResponse,
  ScheduleDeliverableParams,
  ThunkError
>('deliverables/scheduleDeliverable', async (params, { rejectWithValue }) => {
  try {
    toastMessage.info('Scheduling Deliverable...');
    const response =
      await apiInstance.runDeliverables.scheduleDeliverable(params);
    if (!response.success) {
      throw new Error(response.error_msg || 'Deliverable scheduling failed');
    }
    toastMessage.success('Deliverable scheduled successfully');
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_msg ||
      'An unknown error occurred while scheduling deliverable';
    toastMessage.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const toggleReportTypeThunk = createAsyncThunk<
  { companyEntityId: string | number; reportType: DeliverableReportModes },
  { companyEntityId: string | number; reportType: DeliverableReportModes },
  AsyncThunkConfig
>(
  'runDeliverables/toggleReportType',
  async ({ companyEntityId, reportType }) => {
    return { companyEntityId, reportType };
  },
);

export const toggleMemoToggleThunk = createAsyncThunk<
  { companyEntityId: string; memoToggle: MemoSubToggleModes },
  { companyEntityId: string; memoToggle: MemoSubToggleModes },
  AsyncThunkConfig
>(
  'runDeliverables/toggleMemoToggle',
  async ({ companyEntityId, memoToggle }) => {
    return { companyEntityId, memoToggle };
  },
);

export const toggleTransactionSelection = createAsyncThunk<
  {
    companyEntityId: string | number;
    transactionId: string | number;
    selected: boolean;
  },
  {
    companyEntityId: string | number;
    transactionId: string | number;
    selected: boolean;
  },
  AsyncThunkConfig
>(
  'deliverables/toggleTransactionSelection',
  async ({ companyEntityId, transactionId, selected }, { rejectWithValue }) => {
    try {
      return { companyEntityId, transactionId, selected };
    } catch (error: any) {
      return rejectWithValue('Error al actualizar la selección');
    }
  },
);

export const clearReportsTogglesThunk = createAsyncThunk<
  { companyEntityId: string },
  { companyEntityId: string },
  AsyncThunkConfig
>('runDeliverables/clearReportsToggles', async ({ companyEntityId }) => {
  return { companyEntityId };
});

export const clearMemoTogglesThunk = createAsyncThunk<
  { companyEntityId: string },
  { companyEntityId: string },
  AsyncThunkConfig
>('runDeliverables/clearMemoToggles', async ({ companyEntityId }) => {
  return { companyEntityId };
});

export const runDeliverableReports = createAsyncThunk<
  GenericResponse,
  ScheduleDeliverableParams & { isSchedule?: boolean },
  ThunkError
>(
  'deliverables/runDeliverableReports',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      toastMessage.info('Downloading Run Deliverable Report...');
      const { isSchedule, ...deliverableParams } = params;
      const response =
        await apiInstance.runDeliverables.runDeliverableReports(
          deliverableParams,
        );
      if (!response.success) {
        throw new Error(
          response.error_msg || 'Run Deliverable execution failed',
        );
      }
      toastMessage.success('Run Deliverable Report downloaded successfully');
      if (
        typeof response.data === 'object' &&
        response.data !== null &&
        'url' in response.data &&
        'file_name' in response.data
      ) {
        const data = response.data as RunDeliverableResponseData;
        downloadS3File(data.url, data.file_name);
      }
      if (isSchedule && params.schedule_id) {
        (dispatch as any)(fetchReportListDownload({ schedule_id: params.schedule_id }));
      }
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error_msg ||
        'An unknown error occurred while running deliverable';
      toastMessage.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

export const runDeliverableMemos = createAsyncThunk<
  GenericResponse,
  RunDeliverableMemosParams,
  ThunkError
>('deliverables/runDeliverableMemos', async (params, { rejectWithValue }) => {
  try {
    toastMessage.info('Run Deliverable Memos...');
    const response = await apiInstance.runDeliverables.runDeliverableMemos(params);
    if (!response.success) {
        const errorMessage = response.error_msg || 'Run Deliverable Memos execution failed';
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
    }
    toastMessage.success('Run Deliverable Memos executed successfully');

    if (
      typeof response.data === 'object' &&
      response.data !== null &&
      'file_s3_url' in response.data &&
      'file_name' in response.data
    ) {
      const data = response.data as RunDeliverableResponseData;
      downloadS3File(data.file_s3_url, data.file_name);
    } else {
        const errorMessage = 'Invalid response format for Run Deliverable Memos'; 
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
    }
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_msg ||
      'An unknown error occurred while running deliverable memos';
    toastMessage.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const getEntityCurrency = createAsyncThunk<
  EntityJECurrenciesSuccessPayload,
  string | undefined | number,
  ThunkError
>('deliverables/getEntityCurrency', async (entityId, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.runDeliverables.getJECurrenciesByEntity(entityId);
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error_msg ||
      'An unknown error occurred while getting entity currency';
    toastMessage.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const fetchScheduledTransactions = createAsyncThunk<
  { success: boolean; data: Record<string, any[]> },
  { company_entity_ids: string[] },
  ThunkError
>(
  'deliverables/fetchScheduledTransactions',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.hedgeAccounting.getTradesWithDeliverables(params);
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error_msg ||
        'An unknown error occurred while fetching scheduled transactions';
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchBulkTransactionTitlesForDeliverables = createAsyncThunk<
  void,
  BulkTransactionTitlesRequest,
  { dispatch: AppDispatch; rejectValue: string }
>(
  'deliverables/fetchBulkTransactionTitlesForDeliverables',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(fetchBulkTransactionTitles(params));
      if (fetchBulkTransactionTitles.fulfilled.match(result)) {
        const { transaction_titles } = result.payload;
        if (!transaction_titles || transaction_titles.length === 0) {
          toastMessage.warning(
            'No Live/Closed trades found for this entity and no hedge accounting is set up',
          );
        }
      }
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch bulk transaction titles',
      );
    }
  },
);
